import { FieldArray, Formik } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { StateMachineState } from '../../ProgramStateMachine.types'
import { useStateMachineEditorContext } from '../../context/StateMachineEditorContext'
import { toPascalCase } from '../../utils/toPascalCase'
import { CardRow } from '../CardRow'

import { Button, Flex, FormField, MultiSelect, Select, TextInput, Typography } from '@percent/lemonade'

export const SCREAMING_SNAKE_RE = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/

export const AGGREGATION_TYPES = ['sum_of_sum', 'sum_of_average', 'average_of_sum', 'average_of_average'] as const
export type AggregationType = typeof AGGREGATION_TYPES[number]

const AGGREGATED_SCORE_KEY = 'addAggregatedScore'

export interface StateEditFormProps {
  onSubmit: (state: StateMachineState) => void
  onDelete?: () => void
  onCancel?: () => void
  existingStateIds: string[]
  initialValues?: StateMachineState
}

interface SideEffectFormEntry {
  key: string
  values: string
  scoreName: string
  aggregationType: string
  taskTemplateIds: string[]
}

interface FormValues {
  id: string
  displayKey: string
  displayMode: string
  tableType: string
  displayGroup: string
  isTerminal: boolean
  sideEffects: SideEffectFormEntry[]
}

const EMPTY_SIDE_EFFECT_ENTRY: SideEffectFormEntry = {
  key: '',
  values: '',
  scoreName: '',
  aggregationType: '',
  taskTemplateIds: []
}

function parseAggregatedScoreEntry(
  raw: unknown
): Pick<SideEffectFormEntry, 'scoreName' | 'aggregationType' | 'taskTemplateIds'> {
  const e = (raw ?? {}) as { scoreName?: unknown; aggregationType?: unknown; taskTemplateIds?: unknown }

  return {
    scoreName: typeof e.scoreName === 'string' ? e.scoreName : '',
    aggregationType: typeof e.aggregationType === 'string' ? e.aggregationType : '',
    taskTemplateIds: Array.isArray(e.taskTemplateIds)
      ? e.taskTemplateIds.filter((v): v is string => typeof v === 'string')
      : []
  }
}

export function toFormValues(state?: StateMachineState): FormValues {
  const allSideEffects = state?.sideEffects ?? {}
  const sideEffects: SideEffectFormEntry[] = []

  for (const [key, value] of Object.entries(allSideEffects)) {
    if (key === AGGREGATED_SCORE_KEY && Array.isArray(value)) {
      for (const entry of value) {
        sideEffects.push({
          key,
          values: '',
          ...parseAggregatedScoreEntry(entry)
        })
      }
    } else {
      sideEffects.push({
        key,
        values: Array.isArray(value) ? value.join(', ') : '',
        scoreName: '',
        aggregationType: '',
        taskTemplateIds: []
      })
    }
  }

  return {
    id: state?.id ?? '',
    displayKey: state?.metadata?.displayKey ?? '',
    displayMode: state?.metadata?.displayMode ?? 'enabled',
    tableType: state?.metadata?.tableType ?? '',
    displayGroup: state?.metadata?.displayGroup ?? '',
    isTerminal: state?.isTerminal ?? false,
    sideEffects
  }
}

export function fromFormValues(values: FormValues, existingState?: StateMachineState): StateMachineState {
  const sideEffects: Record<string, unknown[]> = {}
  const aggregatedEntries: { scoreName: string; aggregationType: string; taskTemplateIds: string[] }[] = []

  for (const entry of values.sideEffects) {
    const key = entry.key.trim()

    if (key === AGGREGATED_SCORE_KEY) {
      if (entry.scoreName.trim() && entry.aggregationType && entry.taskTemplateIds.length > 0) {
        aggregatedEntries.push({
          scoreName: entry.scoreName.trim(),
          aggregationType: entry.aggregationType,
          taskTemplateIds: entry.taskTemplateIds
        })
      }
    } else if (key) {
      const arr = entry.values
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)

      if (arr.length > 0) {
        sideEffects[key] = arr
      }
    }
  }

  if (aggregatedEntries.length > 0) {
    sideEffects[AGGREGATED_SCORE_KEY] = aggregatedEntries
  }

  return {
    id: values.id,
    metadata: {
      displayKey: values.displayKey || values.id,
      displayMode: values.displayMode || undefined,
      tableType: values.tableType || null,
      displayGroup: values.displayGroup || undefined
    },
    transitions: existingState?.transitions ?? [],
    ...(Object.keys(sideEffects).length > 0 ? { sideEffects } : {}),
    ...(values.isTerminal ? { isTerminal: true } : {})
  }
}

export function StateEditForm({
  onSubmit,
  onDelete,
  onCancel,
  existingStateIds,
  initialValues
}: Readonly<StateEditFormProps>) {
  const { t } = useTranslation()
  const { localDefinition, metadata } = useStateMachineEditorContext()
  const isEditMode = !!initialValues

  const sideEffectValueOptions: Record<string, { value: string; label: string }[]> = useMemo(
    () => ({
      tasks: metadata.taskTemplates.map(tmpl => ({ value: tmpl.id, label: tmpl.name })),
      email: metadata.emailTemplates.map(name => ({ value: name, label: name })),
      slackNotifications: metadata.slackTemplates.map(name => ({ value: name, label: name }))
    }),
    [metadata]
  )

  const displayModeOptions = metadata.displayModes.map(mode => ({
    value: mode,
    label: t(`stateMachine.stateEdit.displayMode${mode.charAt(0).toUpperCase()}${mode.slice(1)}` as any)
  }))

  const tableTypeOptions = metadata.tableTypes.map(type => ({
    value: type,
    label: t(`stateMachine.stateEdit.tableType${toPascalCase(type)}` as any)
  }))

  const sideEffectKeyOptions = [
    { value: 'tasks', label: t('stateMachine.stateEdit.sideEffectTasks') },
    { value: 'email', label: t('stateMachine.stateEdit.sideEffectEmail') },
    { value: 'slackNotifications', label: t('stateMachine.stateEdit.sideEffectSlack') },
    { value: 'donation', label: t('stateMachine.stateEdit.sideEffectDonation') },
    { value: 'openedToNonprofit', label: t('stateMachine.stateEdit.sideEffectOpenedToNonprofit') },
    { value: AGGREGATED_SCORE_KEY, label: t('stateMachine.stateEdit.sideEffectAggregatedScore') }
  ]

  const aggregationTypeOptions = AGGREGATION_TYPES.map(type => ({
    value: type,
    label: t(`stateMachine.stateEdit.aggregationType${toPascalCase(type)}` as any)
  }))

  const taskTemplateOptions = useMemo(
    () => metadata.taskTemplates.map(tmpl => ({ value: tmpl.id, label: tmpl.name })),
    [metadata.taskTemplates]
  )

  const displayGroupOptions = useMemo(() => {
    const names = new Set<string>()
    for (const s of localDefinition?.states ?? []) {
      if (s.metadata?.displayGroup) names.add(s.metadata.displayGroup)
    }

    const selfName = initialValues?.metadata?.displayKey || initialValues?.id

    if (selfName) names.add(selfName)

    return [...names].map(name => ({ value: name, label: name }))
  }, [localDefinition, initialValues])

  const isInGroup = !!initialValues?.metadata?.displayGroup

  const validate = (values: FormValues) => {
    const errors: Record<string, string> = {}

    if (!values.id) {
      errors.id = t('stateMachine.stateEdit.validation.idRequired')
    } else if (!SCREAMING_SNAKE_RE.test(values.id)) {
      errors.id = t('stateMachine.stateEdit.validation.idFormat')
    } else if (!isEditMode && existingStateIds.includes(values.id)) {
      errors.id = t('stateMachine.stateEdit.validation.idUnique')
    }

    if (!values.displayKey) {
      errors.displayKey = t('stateMachine.stateEdit.validation.displayKeyRequired')
    }

    return errors
  }

  return (
    <Formik<FormValues>
      initialValues={toFormValues(initialValues)}
      validate={validate}
      onSubmit={values => {
        onSubmit(fromFormValues(values, initialValues))
      }}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => (
        <Flex direction="column" flex={1} miw={0} mih={0}>
          <Flex direction="column" gap="md" flex={1} mih={0} pb={8} style={{ overflowY: 'auto' }}>
            <FormField
              label={t('stateMachine.stateEdit.stateId')}
              status={touched.id && errors.id ? 'danger' : 'default'}
              statusMessage={touched.id && errors.id ? errors.id : ''}
              necessity="required"
            >
              <TextInput
                name="id"
                value={values.id}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isEditMode}
                placeholder="STATE_NAME"
              />
            </FormField>

            <FormField
              label={t('stateMachine.stateEdit.displayKey')}
              status={touched.displayKey && errors.displayKey ? 'danger' : 'default'}
              statusMessage={touched.displayKey && errors.displayKey ? errors.displayKey : ''}
              necessity="required"
            >
              <TextInput
                name="displayKey"
                value={values.displayKey}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Display name"
              />
            </FormField>

            <FormField label={t('stateMachine.stateEdit.displayMode')}>
              <Select
                name="displayMode"
                options={displayModeOptions}
                defaultValue={displayModeOptions.find(o => o.value === values.displayMode) ?? displayModeOptions[0]}
                onChange={option => setFieldValue('displayMode', option?.value ?? 'enabled')}
              />
            </FormField>

            {!isInGroup && (
              <FormField label={t('stateMachine.stateEdit.tableType')}>
                <Select
                  name="tableType"
                  options={tableTypeOptions}
                  defaultValue={tableTypeOptions.find(o => o.value === values.tableType)}
                  onChange={option => setFieldValue('tableType', option?.value ?? '')}
                  placeholder={t('stateMachine.stateEdit.selectTableType')}
                />
              </FormField>
            )}

            <FormField label={t('stateMachine.stateEdit.displayGroup')}>
              <Select
                name="displayGroup"
                options={[{ value: '', label: t('stateMachine.stateEdit.noGroup') }, ...displayGroupOptions]}
                defaultValue={displayGroupOptions.find(o => o.value === values.displayGroup)}
                onChange={option => setFieldValue('displayGroup', option?.value ?? '')}
                placeholder={t('stateMachine.stateEdit.selectGroup')}
              />
            </FormField>

            <Flex align="center" gap="xs">
              <input
                type="checkbox"
                name="isTerminal"
                checked={values.isTerminal}
                onChange={handleChange}
                data-testid="is-terminal-checkbox"
              />
              <Typography>{t('stateMachine.stateEdit.isTerminal')}</Typography>
            </Flex>

            <Flex direction="column" gap="s">
              <Typography variant="h4">{t('stateMachine.stateEdit.sideEffects')}</Typography>
              <FieldArray name="sideEffects">
                {({ push, remove }) => (
                  <Flex direction="column" gap="xs">
                    {values.sideEffects.map((effect, sideEffectIndex) => (
                      <CardRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={sideEffectIndex}
                        onRemove={() => remove(sideEffectIndex)}
                        removeLabel={t('button.remove')}
                        removeTestId={`remove-side-effect-${sideEffectIndex}`}
                        testId={`side-effect-row-${sideEffectIndex}`}
                      >
                        <Flex wrap="wrap" gap="xs" align="flex-end" style={{ minWidth: 0 }}>
                          <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                            <FormField label={t('stateMachine.stateEdit.sideEffectKey')}>
                              <Select
                                name={`sideEffects.${sideEffectIndex}.key`}
                                options={sideEffectKeyOptions}
                                defaultValue={sideEffectKeyOptions.find(
                                  o => o.value === values.sideEffects[sideEffectIndex].key
                                )}
                                onChange={option =>
                                  setFieldValue(`sideEffects.${sideEffectIndex}.key`, option?.value ?? '')
                                }
                                placeholder={t('stateMachine.stateEdit.selectSideEffectKey')}
                              />
                            </FormField>
                          </div>
                          {effect.key === AGGREGATED_SCORE_KEY ? (
                            <>
                              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                <FormField label={t('stateMachine.stateEdit.aggregatedScoreScoreName')}>
                                  <TextInput
                                    name={`sideEffects.${sideEffectIndex}.scoreName`}
                                    value={values.sideEffects[sideEffectIndex].scoreName}
                                    onChange={handleChange}
                                    placeholder={t('stateMachine.stateEdit.aggregatedScoreScoreNamePlaceholder')}
                                  />
                                </FormField>
                              </div>
                              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                <FormField label={t('stateMachine.stateEdit.aggregatedScoreAggregationType')}>
                                  <Select
                                    name={`sideEffects.${sideEffectIndex}.aggregationType`}
                                    options={aggregationTypeOptions}
                                    defaultValue={aggregationTypeOptions.find(
                                      o => o.value === values.sideEffects[sideEffectIndex].aggregationType
                                    )}
                                    onChange={option =>
                                      setFieldValue(
                                        `sideEffects.${sideEffectIndex}.aggregationType`,
                                        option?.value ?? ''
                                      )
                                    }
                                    placeholder={t('stateMachine.stateEdit.selectAggregationType')}
                                  />
                                </FormField>
                              </div>
                              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                                <FormField label={t('stateMachine.stateEdit.aggregatedScoreTaskTemplates')}>
                                  <MultiSelect
                                    name={`sideEffects.${sideEffectIndex}.taskTemplateIds`}
                                    options={taskTemplateOptions}
                                    defaultValue={taskTemplateOptions.filter(o =>
                                      values.sideEffects[sideEffectIndex].taskTemplateIds.includes(o.value)
                                    )}
                                    onChange={selected =>
                                      setFieldValue(
                                        `sideEffects.${sideEffectIndex}.taskTemplateIds`,
                                        selected.map(o => o.value)
                                      )
                                    }
                                    placeholder={t('stateMachine.stateEdit.selectTaskTemplates')}
                                  />
                                </FormField>
                              </div>
                            </>
                          ) : (
                            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                              <FormField label={t('stateMachine.stateEdit.sideEffectValues')}>
                                {sideEffectValueOptions[effect.key]?.length > 0 ? (
                                  <MultiSelect
                                    name={`sideEffects.${sideEffectIndex}.values`}
                                    options={sideEffectValueOptions[effect.key]}
                                    defaultValue={sideEffectValueOptions[effect.key].filter(o =>
                                      values.sideEffects[sideEffectIndex].values
                                        .split(',')
                                        .map(v => v.trim())
                                        .filter(Boolean)
                                        .includes(o.value)
                                    )}
                                    onChange={selected =>
                                      setFieldValue(
                                        `sideEffects.${sideEffectIndex}.values`,
                                        selected.map(o => o.value).join(', ')
                                      )
                                    }
                                    placeholder={t('stateMachine.stateEdit.selectValues')}
                                  />
                                ) : (
                                  <TextInput
                                    name={`sideEffects.${sideEffectIndex}.values`}
                                    value={values.sideEffects[sideEffectIndex].values}
                                    onChange={handleChange}
                                    placeholder="value1, value2"
                                  />
                                )}
                              </FormField>
                            </div>
                          )}
                        </Flex>
                      </CardRow>
                    ))}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => push({ ...EMPTY_SIDE_EFFECT_ENTRY })}
                      data-testid="add-side-effect"
                    >
                      {t('stateMachine.stateEdit.addSideEffect')}
                    </Button>
                  </Flex>
                )}
              </FieldArray>
            </Flex>

            {isEditMode && onDelete && (
              <Flex>
                <Button variant="reject" onClick={onDelete} data-testid="state-edit-delete">
                  {t('stateMachine.stateEdit.deleteStage')}
                </Button>
              </Flex>
            )}
          </Flex>

          <Flex
            gap="xs"
            justify="flex-end"
            pt={12}
            style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0' }}
            data-testid="state-edit-footer"
          >
            {onCancel && (
              <Button variant="secondary" onClick={onCancel} data-testid="state-edit-cancel">
                {t('button.cancel')}
              </Button>
            )}
            <Button variant="primary" onClick={submitForm} data-testid="state-edit-submit">
              {isEditMode ? t('button.save') : t('stateMachine.stateEdit.addState')}
            </Button>
          </Flex>
        </Flex>
      )}
    </Formik>
  )
}
